import { Injectable } from '@angular/core';
import moment from 'moment';
import {
  DateFormat,
  TimeFormat,
} from '../../components/header/components/interface/interface';
import { StorageBrowser } from '../storage.service';
import { UserService } from '../user.service';

@Injectable({
  providedIn: 'root',
})
export class ConversionDateTimeService {
  public preferredTimeFormat: string;
  private is12HourFormat: boolean;
  public preferredDateFormat: string;
  private preferredTimeFormatPrecision: string;

  constructor(public userService: UserService, private storageBrowser: StorageBrowser) {
    const currUser = this.userService.currentUser.getValue() || {};
    this.is12HourFormat = currUser.preferredTimeFormat !== TimeFormat.H24;
    this.preferredTimeFormat =
      currUser.preferredTimeFormat === TimeFormat.H12 ? 'hh:mm A' : 'HH:mm';
    this.preferredTimeFormatPrecision =
      currUser.preferredTimeFormat === TimeFormat.H12
        ? 'hh:mm:ss A'
        : 'HH:mm:ss';
    this.preferredDateFormat = currUser.preferredDateFormat || DateFormat.MDY;
  }

  convertTime(value: string | Date | number, precision = false): string {
    if (precision) {
      return value
        ? moment(value).format(this.preferredTimeFormatPrecision)
        : '';
    }
    return value ? moment(value).format(this.preferredTimeFormat) : '';
  }

  convertDate(value: string | Date | number, precision = false): string {
    if (!value) return '';
    const date = moment(value);
    return date.isSame(moment(), 'day')
      ? `Today`
      : date?.format(this.preferredDateFormat);
  }
  convertFormattedDate(
    value: string | Date | number,
    precision = false
  ): string {
    if (!value) return '';
    const date = moment(value);
    return date?.format(this.preferredDateFormat);
  }

  convertFormattedDateOffset(
    value: string | Date | number,
    offsetMinutes
  ): string {
    if (!value) return '';
    const date = moment(value).utcOffset(offsetMinutes);
    return date?.format(this.preferredDateFormat);
  }

  convertTimeOffset(value: string | Date | number, offsetMinutes, isUtc = false): string {
    if (!value) return '';
     let date: moment.Moment;

  // handle time-only string (HH:mm or HH:mm:ss)
  if (typeof value === 'string' && /^\d{2}:\d{2}(:\d{2})?$/.test(value)) {
   date = isUtc
      ? moment.utc(value, ['HH:mm:ss', 'HH:mm'], true)
      : moment(value, ['HH:mm:ss', 'HH:mm'], true);
  } else {
      date = isUtc ? moment.utc(value) : moment(value);
  }

  if (!date.isValid()) return '';

  return date.utcOffset(offsetMinutes).format(this.preferredTimeFormat);
  }

  convertFormattedDateTime(date: moment.Moment, precision = false): string {
    if (!date) return '';
    return `${date?.format(this.preferredDateFormat)} ${date?.format(
      this.preferredTimeFormat
    )}`;
  }

  convertDateTime(value: string | Date | number, precision = false): string {
    if (!value) return '';
    return `${this.convertDate(value, precision)} ${this.convertTime(
      value,
      precision
    )}`;
  }

  convertDateTimeOffset(value: string | Date | number, offsetMinutes: number, precision = false): string {
    if (!value) return '';
    const date = moment.utc(value).utcOffset(offsetMinutes);
    const datePart = date.isSame(moment(), 'day')
      ? 'Today'
      : date.format(this.preferredDateFormat);
    const timePart = date.format(
      precision ? this.preferredTimeFormatPrecision : this.preferredTimeFormat
    );
    return `${datePart} ${timePart}`;
  }

  convertTimeOffsetPrecision(value: string | Date | number, offsetMinutes: number): string {
    if (!value) return '';
    const date = moment.utc(value).utcOffset(offsetMinutes);
    if (!date.isValid()) return '';
    return date.format(this.preferredTimeFormatPrecision);
  }
}
